import React from 'react';
import moment from 'moment';

const ClassicEleganceTemplate = ({ packageData, user }) => {
  if (!packageData?.package) return <div>Loading package data...</div>;
  const { package: pkg } = packageData;

  const styles = {
    container: { fontFamily: "'Lora', 'Times New Roman', serif", color: "#333", backgroundColor: "#fff", padding: "40px" },
    header: { textAlign: 'center', marginBottom: '30px', borderBottom: '1px solid #eee', paddingBottom: '20px' },
    logo: { maxHeight: '100px', marginBottom: '15px' },
    title: { fontSize: '2.8rem', fontWeight: '700', color: '#1a1a1a', marginBottom: '5px', letterSpacing: '1px' },
    destinations: { fontSize: '1.3rem', color: '#666', marginBottom: '25px', fontStyle: 'italic' },
    mainImage: { width: '100%', height: '400px', objectFit: 'cover', borderRadius: '8px', marginBottom: '30px' },
    section: { marginBottom: '40px' },
    sectionTitle: { fontSize: '1.6rem', fontWeight: '700', color: '#2c3e50', borderBottom: '2px solid #e0e0e0', paddingBottom: '10px', marginBottom: '20px' },
    summaryGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '1.1rem' },
    summaryItem: { padding: '10px', borderLeft: '3px solid #bdc3c7', backgroundColor: '#f9f9f9' },
    itineraryItem: { marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px dashed #ccc' },
    itineraryTitle: { color: '#2c3e50', fontSize: '1.2rem', fontWeight: 'bold' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' },
    th: { backgroundColor: '#f2f2f2', padding: '12px', border: '1px solid #ddd', textAlign: 'left', fontWeight: 'bold' },
    td: { padding: '12px', border: '1px solid #ddd' },
    listGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' },
    list: { paddingLeft: '20px' },
    listItem: { marginBottom: '8px' },
    termsTitle: { fontWeight: 'bold', color: '#2c3e50', fontSize: '1.1rem' },
    footer: { textAlign: 'center', marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #eee', fontSize: '0.9rem', color: '#777' }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        {user?.logo && <img src={user.logo} alt="Logo" style={styles.logo} />}
        <h1 style={styles.title}>{pkg.packageName}</h1>
        <p style={styles.destinations}>{pkg.destinationsCover}</p>
      </header>
      
      <img src={pkg.imageSrc} alt={pkg.packageName} style={styles.mainImage} />

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Journey Summary</h2>
        <div style={styles.summaryGrid}>
          <div style={styles.summaryItem}><strong>Duration:</strong> {pkg.duration}</div>
          <div style={styles.summaryItem}><strong>Start Date:</strong> {moment(pkg.startedDate).format("MMMM Do, YYYY")}</div>
          <div style={styles.summaryItem}><strong>Destinations:</strong> {pkg.destinationsCover}</div>
          <div style={styles.summaryItem}><strong>Price:</strong> {pkg.price} {pkg.currency}</div>
        </div>
      </section>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Overview</h2>
        <p style={{ lineHeight: '1.8', fontSize: '1.1rem' }}>{pkg.description}</p>
      </section>

      {packageData.itineraries?.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Daily Itinerary</h2>
          {packageData.itineraries.slice().sort((a, b) => new Date(a.date) - new Date(b.date)).map((item, index) => (
            <div key={index} style={styles.itineraryItem}>
              <h4 style={styles.itineraryTitle}>{moment(item.date).format("dddd, MMMM Do")} - {item.title}</h4>
              <p style={{ margin: '5px 0' }}><strong>Location:</strong> {item.location}</p>
              <p style={{ margin: '5px 0' }}>{item.description}</p>
              {item.meals && <p style={{ margin: '5px 0', fontSize: '0.9rem', color: '#555' }}><strong>Meals:</strong> {item.meals}</p>}
            </div>
          ))}
        </section>
      )}
      
      {/* --- FLIGHT DETAILS --- */}
      {packageData.flightBookings?.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Flight Details</h2>
          <table style={styles.table}>
            <thead><tr><th style={styles.th}>Airline</th><th style={styles.th}>From</th><th style={styles.th}>To</th><th style={styles.th}>Departure</th><th style={styles.th}>Arrival</th></tr></thead>
            <tbody>
              {packageData.flightBookings.map((flight, i) => (
                <tr key={i}>
                  <td style={styles.td}>{flight.airline} {flight.flightNumber}</td>
                  <td style={styles.td}>{flight.departureCity}</td>
                  <td style={styles.td}>{flight.arrivalCity}</td>
                  <td style={styles.td}>{moment(flight.departureDateTime).format("MMM Do, h:mm a")}</td>
                  <td style={styles.td}>{moment(flight.arrivalDateTime).format("MMM Do, h:mm a")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* --- HOTEL BOOKINGS --- */}
      {packageData.hotelBookings?.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Accommodations</h2>
          <table style={styles.table}>
            <thead><tr><th style={styles.th}>Hotel</th><th style={styles.th}>City</th><th style={styles.th}>Check-In</th><th style={styles.th}>Check-Out</th><th style={styles.th}>Room Type</th></tr></thead>
            <tbody>
              {packageData.hotelBookings.map((hotel, i) => (
                <tr key={i}>
                  <td style={styles.td}>{hotel.hotelName}</td>
                  <td style={styles.td}>{hotel.city}</td>
                  <td style={styles.td}>{moment(hotel.checkInDate).format("MMM Do, YYYY")}</td>
                  <td style={styles.td}>{moment(hotel.checkOutDate).format("MMM Do, YYYY")}</td>
                  <td style={styles.td}>{hotel.roomType}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      
      {/* --- SIGHTSEEING DETAILS --- */}
      {packageData.sightSeeings?.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Sightseeing Itinerary</h2>
          <table style={styles.table}>
            <thead><tr><th style={styles.th}>Date</th><th style={styles.th}>Attraction</th><th style={styles.th}>Location</th></tr></thead>
            <tbody>
              {packageData.sightSeeings.map((sight, i) => (
                <tr key={i}>
                  <td style={styles.td}>{moment(sight.date).format("MMM Do, YYYY")}</td>
                  <td style={styles.td}>{sight.sightseeingName}</td>
                  <td style={styles.td}>{sight.destination}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      
      {/* --- TRANSFER DETAILS --- */}
      {packageData.transfers?.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Transfers</h2>
          <table style={styles.table}>
            <thead><tr><th style={styles.th}>Date & Time</th><th style={styles.th}>From</th><th style={styles.th}>To</th><th style={styles.th}>Vehicle</th></tr></thead>
            <tbody>
              {packageData.transfers.map((transfer, i) => (
                <tr key={i}>
                  <td style={styles.td}>{moment(transfer.date).format("MMM Do")} at {transfer.pickupTime}</td>
                  <td style={styles.td}>{transfer.fromLocation}</td>
                  <td style={styles.td}>{transfer.toLocation}</td>
                  <td style={styles.td}>{transfer.vehicleDetails}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      
      {/* --- INCLUDES & EXCLUDES --- */}
      {(packageData.includes?.length > 0 || packageData.excludes?.length > 0) && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Package Inclusions & Exclusions</h2>
          <div style={styles.listGrid}>
            {packageData.includes?.length > 0 && (
              <div>
                <h4 style={{ color: '#2c3e50' }}>What's Included</h4>
                <ul style={styles.list}>
                  {packageData.includes.map((item, i) => <li key={i} style={styles.listItem}>{item.description}</li>)}
                </ul>
              </div>
            )}
            {packageData.excludes?.length > 0 && (
              <div>
                <h4 style={{ color: '#2c3e50' }}>What's Not Included</h4>
                <ul style={styles.list}>
                  {packageData.excludes.map((item, i) => <li key={i} style={styles.listItem}>{item.description}</li>)}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}
      
      {/* --- TERMS AND CONDITIONS --- */}
      {packageData.termsandConditions?.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Terms & Conditions</h2>
          {packageData.termsandConditions.map((term, index) => (
            <div key={index} style={{ marginBottom: '15px' }}>
              <h5 style={styles.termsTitle}>{term.title}</h5>
              <p style={{ lineHeight: '1.6' }}>{term.description}</p>
            </div>
          ))}
        </section>
      )}

      <footer style={styles.footer}>
        <p>This quotation was prepared for you by {user?.companyName || 'our team'}.</p>
        <p>For any questions, please contact us at {pkg.contactEmail}.</p>
      </footer>
    </div>
  );
};

export default ClassicEleganceTemplate;